// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useEffect, useState } from 'react';
import { bool, func, string, object, number, arrayOf } from 'prop-types';
import get from 'lodash/get';
import { useIntl } from 'react-intl';
import {
  Button,
  Dialog,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  withStyles,
} from '@material-ui/core';
import { compose } from 'recompose';
import { connect } from 'react-redux';

import { TablePagination } from '../../../../components';
import SingleRow from './SingleRow';
import { getI18n, getI18nFormattedMessage } from '../../../../utils/intl';
import {
  deleteKey,
  regenerateSecret,
  registerKey,
} from '../../../../actions/application';
import {
  fetchAuthProvidersList,
} from '../../../../actions/authProviders';
import {
  getAuthProvidersList,
} from '../../../../reducers/authProviders';
import {
  fetchListDetails,
} from '../../../../actions/requests';

import {
  AUTHENTICATION_LABEL,
  KEY_NAME_LABEL,
  STATUS_LABEL,
  DAYS_TO_KEY_EXPIRY_LABEL,
} from '../labels';
import styles from './styles';
import {
  ALERT_ERROR,
  ALERT_SUCCESS,
  API_KEY_STATUS_DELETE_FAILED,
  APPLICATION_STATUS_DELETE_FAILED,
  APPLICATION_STATUS_DELETE_PENDING_APPROVAL,
  APPLICATION_STATUS_DISABLED,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_REJECTED,
  FAIL,
  SUCCESS,
} from '../../../../constants';
import EditKey from '../EditKey';
import {
  getDeleteKeyErrors,
  getDeleteKeyStatus,
  getRegeneratedSecretDetails,
  getRegerateSecretStatus,
  getRegisterKeyStatus,
  getRegisteredKeyDetails,
  getDeleteOAuthClientStatus,
  getRegisterKeyError,
} from '../../../../reducers/application';

import { hasCRUDPermissions } from '../../../../utils';
import { API_KEYS_APPLICATION } from '../../../../constants';

const GRID_ROWS_PER_PAGE_OPTIONS = [10, 20];
const GRID_ROWS_PER_PAGE_DEFAULT_OPTION = 10;

const addKeyDisabledStates = [
  APPLICATION_STATUS_DELETE_FAILED,
  APPLICATION_STATUS_DELETE_PENDING_APPROVAL,
  APPLICATION_STATUS_DISABLED,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_REJECTED,
];

export const shouldDisableAddKeyBtn = (applicationStatus, isEditDisabled, locked) => {
  return (addKeyDisabledStates.includes(applicationStatus)) || isEditDisabled || locked;
}

export const ConfigurationRaw = ({
  applicationUuid,
  fetchSingleApp,
  fetchApplicationKeys,
  appKeys,
  appKeysTotalPages,
  appKeysTotalElements,
  authProviders,
  isMultiKeySupport,
  classes,
  notifyMessages,
  isAdmin,
  appSecretHashingMetadata,
  locked = false,
  isLocking,
  disabledByInternal,
  onSaveDone,
  deleteKey,
  deleteOAuthClient,
  apiKeySettings,
  regenerateSecret,
  registerKey,
  regeneratedSecretDetails,
  regerateSecretStatus,
  registeredKeyDetails,
  registerKeyStatus,
  registerKeyError,
  isEditDisabled,
  isPortalAdmin,
  deleteKeyStatus,
  deleteKeyErrors,
  certificateList,
  earliestExpiryCertTs,
  fetchAuthProvidersList,
  user,
  applicationDetails,
  editPendingApiKeys,
  fetchListDetails,
  deleteOAuthClientStatus,
}) => {
  const intl = getI18n(useIntl());
  const rows = isMultiKeySupport ? appKeys : [];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [isEditKeyOpen, setIsEditKeyOpen] = useState(false);
  const [expandIndex, setExpandIndex] = useState(0);
  const [defaultHashType, setDefaultHashType] = useState('');

  const canAddKey = hasCRUDPermissions(user, ['CREATE'], API_KEYS_APPLICATION);
  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    fetchApplicationKeys(applicationUuid, newPage, rowsPerPage);
  };

  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };
  const addNewKey = () => {
    setIsEditKeyOpen(true);
  };

  const isApiKeyExpiryEnabled = get(apiKeySettings, 'enabled', false);
  const startReportingDays = get(apiKeySettings, 'startReportingBeforeDays', 90);
  const urgentReportingDays = get(apiKeySettings, 'reportAsUrgentBeforeDays', 60);

  useEffect(() => {
    if (!get(appSecretHashingMetadata, 'plaintextAllowed')) {
      if (get(appSecretHashingMetadata, 'algorithm')) {
        setDefaultHashType('HASHED_SECRET');
      } else {
        setDefaultHashType('PLAIN_SECRET');
      }
    } else {
      setDefaultHashType('HASHED_SECRET');
    }
  }, [appSecretHashingMetadata]);

  useEffect(() => {
    fetchAuthProvidersList();
  }, []);

  useEffect(() => {
    if(isAdmin && appKeys[expandIndex] &&
       appKeys[expandIndex].status === API_KEY_STATUS_DELETE_FAILED) {
      if(deleteKeyStatus === 'SUCCESS') {
        fetchSingleApp(applicationUuid);
        notifyMessages(intl.getI18nMessage('label.application.details.keys.key.delete.success'), ALERT_SUCCESS, true);
      } else if (deleteKeyStatus === 'FAIL') {
        if (deleteKeyErrors.length > 0 && (deleteKeyErrors[0].field === 'apiKey' || deleteKeyErrors[0].field === 'keyStoreCheck')) {
          notifyMessages(deleteKeyErrors[0].error, ALERT_ERROR, true);
        } else {
          notifyMessages(intl.getI18nMessage('label.application.details.keys.key.delete.error'), ALERT_ERROR, false);
        }
      }
    }
  },[deleteKeyStatus]);

  useEffect(() => {
    if(isAdmin && appKeys[expandIndex] &&
       appKeys[expandIndex].status === API_KEY_STATUS_DELETE_FAILED) {
      if(deleteOAuthClientStatus === SUCCESS) {
        fetchApplicationKeys(applicationUuid);
        notifyMessages(intl.getI18nMessage('label.application.details.keys.oauth.client.delete.success'), ALERT_SUCCESS, true);
      } else if (deleteOAuthClientStatus === FAIL) {
        if (deleteKeyErrors.length > 0 && (deleteKeyErrors[0].field === 'apiKey' || deleteKeyErrors[0].field === 'keyStoreCheck')) {
          notifyMessages(deleteKeyErrors[0].error, ALERT_ERROR, false);
        } else {
          notifyMessages(intl.getI18nMessage('label.application.details.keys.oauth.client.delete.error'), ALERT_ERROR, false);
        }
      }
    }
  },[deleteOAuthClientStatus]);

  const onSaveKey = () => {
    onSaveDone();
    fetchListDetails({ entityUuid: applicationUuid });
  }
  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchApplicationKeys(applicationUuid, 0, newRowsPerPage);
  };

  const addKeyDetails = {
    name : null,
    status : 'ENABLED',
    defaultKey : false,
    callbackUrl : null,
    oauthCallbackUrl: null,
    oauthScope : null,
    oauthType : 'PUBLIC',
    apiKey : '',
    keySecret : '',
    isHashed : defaultHashType === 'HASHED_SECRET',
    isRegenerateSecret : false,
  }

  const isAddKeyDisabled =
        shouldDisableAddKeyBtn(applicationDetails.status, isEditDisabled, locked);

  return (
    <div id="app-configuration-container" className={classes.appConfigurationContainer}>
        <Fragment>
          <Dialog
            open={isEditKeyOpen}
            onClose={() => setIsEditKeyOpen(false)}
            aria-labelledby="edit-key-dialog"
            id={"new-api-key"}
            classes={{
              paper: classes.paperClass,
            }}
          >
            <EditKey
              applicationDetails={applicationDetails}
              applicationUuid={applicationUuid}
              authProviders={authProviders}
              details={addKeyDetails}
              closeAction={() => {
                setIsEditKeyOpen(false);
                fetchApplicationKeys(applicationUuid);
              }}
              appSecretHashingMetadata={appSecretHashingMetadata}
              notifyMessages={notifyMessages}
              keyCount={appKeysTotalElements}
              onSaveDone={onSaveKey}
              isLocking={isLocking}
              isPortalAdmin={isPortalAdmin}
              certificateCount={certificateList ? certificateList.length : 0}
              user={user}
            />
          </Dialog>
          <div className={classes.filtersAndAdd}>
            {canAddKey && <Button
              id="add-key-button"
              data-apim-test="add-key-button"
              variant="contained"
              color="secondary"
              className={classes.addKeyButton}
              onClick={addNewKey}
              disabled={isAddKeyDisabled}
            >
              {getI18nFormattedMessage('label.application.details.keys.addkey.button')}
            </Button>}
          </div>

          <TableContainer className={classes.tableContainer}>
            <Table aria-label="collapsible table">
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell>{KEY_NAME_LABEL}</TableCell>
                  <TableCell>{AUTHENTICATION_LABEL}</TableCell>
                  <TableCell>{STATUS_LABEL}</TableCell>
                  <TableCell>{DAYS_TO_KEY_EXPIRY_LABEL}</TableCell>
                  <TableCell className={classes.expanderColumn}/>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows && rows.length > 0 ? rows.map((row, idx) =>
                  <SingleRow
                    key={idx}
                    row={row}
                    idx={idx}
                    intl={intl}
                    classes={classes}
                    notifyMessages={notifyMessages}
                    isAdmin={isAdmin}
                    appSecretHashingMetadata={appSecretHashingMetadata}
                    applicationDetails={applicationDetails}
                    applicationUuid={applicationUuid}
                    isLocking={isLocking}
                    isApiKeyExpiryEnabled={isApiKeyExpiryEnabled}
                    startReportingDays={startReportingDays}
                    urgentReportingDays={urgentReportingDays}
                    locked={locked}
                    expandIndex={expandIndex}
                    setExpandIndex={setExpandIndex}
                    disabledByInternal={disabledByInternal}
                    onSaveDone={onSaveKey}
                    deleteKey={deleteKey}
                    deleteOAuthClient={deleteOAuthClient}
                    regenerateSecret={regenerateSecret}
                    registerKey={registerKey}
                    regeneratedSecretDetails={regeneratedSecretDetails}
                    regerateSecretStatus={regerateSecretStatus}
                    registeredKeyDetails={registeredKeyDetails}
                    registerKeyStatus={registerKeyStatus}
                    registerKeyError={registerKeyError}
                    defaultHashType={defaultHashType}
                    isEditDisabled={isEditDisabled || editPendingApiKeys?.includes(row.apiKey)}
                    fetchApplicationKeys={fetchApplicationKeys}
                    isPortalAdmin={isPortalAdmin}
                    earliestExpiryCertTs={earliestExpiryCertTs}
                    certificateList={certificateList}
                    authProviders={authProviders}
                    user={user}
                    editPendingApiKeys={editPendingApiKeys}
                    canAddKey={canAddKey}
                  />)
                :<span>{getI18nFormattedMessage('label.application.details.keys.overview.nokeys')}</span>
                }
              </TableBody>
            </Table>
          </TableContainer>
          <div className={classes.paginationContainer}>
            <TablePagination
              id="app-configuration-keys-pagination"
              data-apim-test="app-configuration-keys-pagination"
              page={page}
              count={appKeysTotalElements}
              pageCount={appKeysTotalPages}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={GRID_ROWS_PER_PAGE_OPTIONS}
              onChangeRowsPerPage={onChangeRowsPerPage}
              onChangePage={onChangePage}
              onChangePreviousPage={onChangePreviousPage}
              onChangeNextPage={onChangeNextPage}
              labelRowsPerPage={intl.getI18nMessage('label.pagination.rows.per.page')}
            />
          </div>
        </Fragment>
    </div>
  );
};

const mapStateToProps = state => ({
  regeneratedSecretDetails: getRegeneratedSecretDetails(state),
  regerateSecretStatus: getRegerateSecretStatus(state),
  registerKeyStatus: getRegisterKeyStatus(state),
  registerKeyError: getRegisterKeyError(state),
  registeredKeyDetails: getRegisteredKeyDetails(state),
  deleteKeyStatus: getDeleteKeyStatus(state),
  deleteKeyErrors: getDeleteKeyErrors(state),
  authProviders: getAuthProvidersList(state),
  deleteOAuthClientStatus: getDeleteOAuthClientStatus(state),
});

const mapDispatchToProps = {
  regenerateSecret,
  registerKey,
  deleteKey,
  fetchAuthProvidersList,
  fetchListDetails,
};
ConfigurationRaw.propTypes = {
  applicationUuid: string,
  applicationDetails: object,
  authProviders: arrayOf(object),
  fetchApplicationKeys: func,
  fetchSingleApp: func,
  apiKeySettings: object,
  appKeys: arrayOf(object),
  appKeysTotalPages: number,
  appKeysTotalElements: number,
  fetchAuthProvidersList: func,
  isMultiKeySupport: bool,
  classes: object,
  isAdmin: bool,
  notifyMessages: func,
  appSecretHashingMetadata: object,
  isLocking: bool,
  locked: bool,
  disabledByInternal: bool,
  onSaveDone: func,
  regenerateSecret: func,
  registerKey: func,
  regeneratedSecretDetails: object,
  regerateSecretStatus: string,
  registeredKeyDetails: object,
  registerKeyStatus: string,
  registerKeyError: string,
  deleteKey: func,
  deleteOAuthClient: func,
  isEditDisabled: bool,
  isPortalAdmin: bool,
  deleteKeyStatus: func,
  deleteKeyErrors: arrayOf(object),
  certificateList: arrayOf(object),
  earliestExpiryCertTs: number,
  user: object,
  editPendingApiKeys: arrayOf(string),
  fetchListDetails: func,
  deleteOAuthClientStatus: string,
};
ConfigurationRaw.displayName = 'Configuration';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ConfigurationRaw);
